import { Folder, LinkSimple, List, MagnifyingGlass } from "@phosphor-icons/react"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"

// Solarized-style syntax highlighting
function Var({ children }: { children: string }) {
  return <span className="text-fg-default">{children}</span>
}

function Method({ children }: { children: string }) {
  return <span className="text-[#b58900]">{children}</span>
}

function Str({ children }: { children: string }) {
  return <span className="text-[#cb4b16]">{children}</span>
}

function Op({ children }: { children: string }) {
  return <span className="text-fg-secondary">{children}</span>
}

// Sidebar icon component
function SidebarIcon({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-10 h-10 flex items-center justify-center text-fg-secondary hover:text-fg-default">
      {children}
    </div>
  )
}

// DataFrame data
const dataFrameRows = [
  { index: 61, project_name: "Ethereum", value: "1.936796e+10" },
  { index: 32, project_name: "Bitcoin", value: "4.110616e+09" },
  { index: 191, project_name: "Uniswap", value: "3.682764e+09" },
  { index: 188, project_name: "Tron", value: "2.889465e+09" },
  { index: 126, project_name: "OpenSea", value: "2.566551e+09" },
  { index: 101, project_name: "Lido Finance", value: "1.888454e+09" },
  { index: 66, project_name: "Filecoin", value: "1.683659e+09" },
]

function JupyterNotebookViewer() {
  return (
    <div
      className="relative border-t border-l border-solid border-border-default rounded-tl-xl bg-bg-default"
      style={{
        height: "320px",
        overflow: "hidden",
        ...FADE_MASK_STYLE,
      }}
    >
      <div className="flex">
        {/* Left Sidebar */}
        <div className="w-12 border-r border-border-default flex flex-col items-center py-2 shrink-0">
          <SidebarIcon>
            <List className="w-5 h-5" />
          </SidebarIcon>
          <SidebarIcon>
            <MagnifyingGlass className="w-5 h-5" />
          </SidebarIcon>
          <SidebarIcon>
            <span className="font-mono text-sm font-medium">{"{x}"}</span>
          </SidebarIcon>
          <SidebarIcon>
            <LinkSimple className="w-5 h-5" />
          </SidebarIcon>
          <SidebarIcon>
            <Folder className="w-5 h-5" />
          </SidebarIcon>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4">
          {/* Code Cell */}
          <div className="flex gap-3 mb-6">
            {/* Cell indicator */}
            <div className="text-fg-secondary font-mono text-sm pt-2 shrink-0">[1]</div>

            {/* Code content */}
            <div className="flex-1 bg-bg-inset border border-border-default rounded-lg p-4">
              <div className="font-mono text-sm leading-relaxed whitespace-pre">
                <div>
                  <Var>df</Var>
                  <Op> = </Op>
                  <Var>pd</Var>
                  <Op>.</Op>
                  <Method>DataFrame</Method>
                  <Op>(</Op>
                  <Var>data</Var>
                  <Op>)</Op>
                </div>
                <div>
                  <Var>fees_by_project</Var>
                  <Op> = </Op>
                  <Var>df</Var>
                  <Op>.</Op>
                  <Method>groupby</Method>
                  <Op>(</Op>
                  <Str>{"'project_name'"}</Str>
                  <Op>)[</Op>
                  <Str>{"'value'"}</Str>
                  <Op>].</Op>
                  <Method>sum</Method>
                  <Op>()</Op>
                </div>
                <div>
                  <Var>top_projects</Var>
                  <Op> = </Op>
                  <Var>fees_by_project</Var>
                  <Op>.</Op>
                  <Method>sort_values</Method>
                  <Op>(</Op>
                  <Var>by</Var>
                  <Op>=</Op>
                  <Str>{"'value'"}</Str>
                  <Op>, </Op>
                  <Var>ascending</Var>
                  <Op>...</Op>
                </div>
              </div>
            </div>
          </div>

          {/* Output Cell */}
          <div className="flex gap-3">
            {/* Output indicator */}
            <div className="text-fg-secondary font-mono text-sm pt-1 shrink-0 w-6 flex justify-center">
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M4 6h16M4 12h16M4 18h10" />
              </svg>
            </div>

            {/* Output content */}
            <div className="flex-1">
              <div className="text-fg-default text-sm mb-4 font-mono">
                Top 10 Projects by Fees from September to November 2024:
              </div>

              {/* DataFrame Table */}
              <div className="font-mono text-sm">
                {/* Header */}
                <div className="flex text-fg-secondary mb-1">
                  <span className="w-16 text-right pr-4"></span>
                  <span className="w-32 text-right pr-4">project_name</span>
                  <span className="w-32 text-right">value</span>
                </div>

                {/* Data rows */}
                {dataFrameRows.map((row) => (
                  <div key={row.index} className="flex text-fg-default">
                    <span className="w-16 text-right pr-4 text-fg-secondary">{row.index}</span>
                    <span className="w-32 text-right pr-4">{row.project_name}</span>
                    <span className="w-32 text-right">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export { JupyterNotebookViewer }
